import { loadScript, customerSafeError } from '../loadScript.js';

/**
 * Razorpay Checkout opens as a modal on our own page, so the customer never
 * navigates away and the merchant's brand stays on screen. The card is entered
 * inside Razorpay's frame and never touches us.
 *
 * `open()` resolves with whatever the widget hands back; our server verifies the
 * signature before believing any of it.
 */
export async function open({ session, merchant, payment, onDismiss }) {
  const Razorpay = await loadScript(session.scriptUrl, { globalName: 'Razorpay' });

  return new Promise((resolve, reject) => {
    let settled = false;

    const checkout = new Razorpay({
      key: session.keyId,
      order_id: session.orderId,
      amount: session.amount,
      currency: session.currency,
      // HC-1: the merchant's brand, never ours and never the processor's.
      name: merchant.displayName,
      description: payment.description || undefined,
      image: merchant.logoUrl || undefined,
      theme: { color: merchant.accentColor },
      prefill: session.prefill ?? {},
      // We render our own confirmation, so Razorpay's is redundant.
      modal: {
        confirm_close: true,
        ondismiss: () => {
          if (settled) return;
          settled = true;
          onDismiss?.();
          // Dismissing is not an error — the customer simply closed the window.
          resolve(null);
        },
      },
      handler: (response) => {
        if (settled) return;
        settled = true;
        resolve({
          razorpay_payment_id: response.razorpay_payment_id,
          razorpay_order_id: response.razorpay_order_id,
          razorpay_signature: response.razorpay_signature,
        });
      },
    });

    checkout.on('payment.failed', (event) => {
      if (settled) return;
      settled = true;
      // HC-9: an instruction, never the provider's raw reason.
      reject(customerSafeError(event?.error?.code, event?.error?.description));
    });

    checkout.open();
  });
}

export default { open };
